import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    FilteredValue,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { AgroambientalModel } from '../../../../../../models/tareas/agroambientales.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import { MaquinariaModel } from '../../../../../../models/trabajadores/maquinaria.model';
import { getDropdownSelected, getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';

interface ActividadSobreCubiertaModel {
    codigo_SIEX?: string | number;
    actividad?: string;
    actividad_sobre_la_cubierta?: string;
}

interface EspecieAnimalModel {
    id?: string | number;
    especies_animales?: string;
}

export class AgroambientalesFormDefinition {
    public readonly appName = environment.appName;
    public readonly fincaName = environment.appName === 'avafitogest' ? 'Explotación' : environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelasName = environment.features.parcelasName;
    public readonly tipoSuperficieParcelas = environment.features.hasSuperficieCultivada
        ? 'Sup.\xa0Cultivada'
        : 'S.\xa0Sigpac';
    public readonly textoSuperficie = environment.features.textoSuperficie;
    public readonly hasClients = environment.features.hasClients;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';

    public formType!: FormType;
    public model: Partial<AgroambientalModel> = {};
    public formFields!: Form<AgroambientalModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);
    public maquinarias: DropdownData<MaquinariaModel> = list<MaquinariaModel>(['...', null]);
    public actividadesSobreCubierta: DropdownData<ActividadSobreCubiertaModel> = list<ActividadSobreCubiertaModel>(['...', null]);
    public especiesAnimales: DropdownData<EspecieAnimalModel> = list<EspecieAnimalModel>(['...', null]);

    public tipoEcorregimenList: DropdownData<string> = list<string>('...', 'Leñosos', 'Arroz', 'Pastoreo');
    public tareasLabor: DropdownData<string> = list<string>(
        ['...', null],
        'Fangueo',
        'Hacer Márgenes',
        'Trailla',
        'Láser',
        'Cultivador',
        'Rotovator',
        'Multiapero',
        'Paquete de trabajo 1',
        'Paquete de trabajo 2',
        'Desbroce Cursos de Agua',
    );
    public tipoOperacionList: DropdownData<string> = list<string>('', 'Escarda química', 'Desbroce mecánico');

    public fieldNames: FormField<AgroambientalModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<AgroambientalModel>) {
        this.formType = +type;
        this.model = model;
        this.ensureDefaultDropdownValues();

        this.formFields = {
            name: 'tareas-agroambientales',
            header: `Datos de la tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };

    }

    public syncConditionalVisibility(): void {
        for (const section of this.formFields.sections) {
            for (const field of section.fields) {
                this.applyTipoVisibility(field);
            }
        }
    }

    public isPastoreoSelected(): boolean {
        return this.getTipoEcorregimen() === 'Pastoreo';
    }

    private getFormSections(): FormSection<AgroambientalModel>[] {
        const sections: FormSection<AgroambientalModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: 'Cubiertas Vegetales Espontáneas o Sembrada',
                width: 12,
                isVisible: true,
                fields: this.getLenososFields('Leñosos'),
                order: 2,
            },
            {
                name: 'Cubiertas Inertes (Restos Poda)',
                width: 12,
                isVisible: true,
                fields: this.getCubiertasInertesFields('Leñosos'),
                order: 3,
            },
            {
                name: 'Pastoreo Extensivo',
                width: 12,
                isVisible: true,
                fields: this.getPastoreoExtensivoFields('Pastoreo'),
                order: 4,
            },
            {
                name: 'Pastoreo Siega Sostenible / Islas de Biodiversidad',
                width: 12,
                isVisible: true,
                fields: this.getPastoreoIslasFields('Pastoreo'),
                order: 5,
            },
            {
                name: 'Ecorregímenes Arroz',
                width: 12,
                isVisible: true,
                fields: this.getArrozAvaFields('Arroz'),
                order: 6,
            },
            {
                name: 'Arroz',
                width: 12,
                isVisible: this.appName === 'sequiador' || this.appName === 'SIEX' || this.formType === FormType.EXPORT,
                fields: this.getArrozFields('Arroz'),
                order: 7,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 8,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<AgroambientalModel>[] {
        return this.pushFields([
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                vinculatedFields: undefined,
                label: this.fincaName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.fincas,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterFinca(it),
                        },
                    } : {}),
                },
                width: 4,
                conditions: this.conditions({ isRequired: this.formType < FormType.DELETION }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.sectorName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.MULTISELECT_SEARCH : InputType.ONLY_TEXT,
                    data: this.sectores,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterSector(it),
                        },
                    } : {}),
                },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.parcelasName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.MULTISELECT_SEARCH : InputType.ONLY_TEXT,
                    data: this.parcelas,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterParcela(it),
                            summation: {
                                summand: environment.features.hasSuperficieCultivada
                                    ? 'superficie_cultivada'
                                    : 'superficie_sigpac',
                                name: this.tipoSuperficieParcelas,
                                uds: this.textoSuperficie,
                                format: (total: number): string => total.toLocaleString('es-ES', {
                                    useGrouping: true,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }),
                            },
                        },
                    } : {}),
                },
                width: 4,
                conditions: this.conditions({ isRequired: this.formType < FormType.DELETION }),
                order: 3,
            },
            {
                name: 'tipo_ecorregimen',
                vinculatedFields: undefined,
                label: 'Tipo de Registro Ecorrégimen',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tipoEcorregimenList,
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 4,
            },
        ]);
    }

    private getLenososFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_establecimiento_cv', 'Fecha Establecimiento Cubierta Vegetal', 1, tipo, 3, this.getLenosCalendarLimits('cv')),
            this.numericField('anchura_cv', 'Anchura Cubierta Vegetal (m)', 2, tipo),
            {
                name: 'tipo_mantenimiento_cv',
                vinculatedFields: undefined,
                label: 'Tipo Mantenimiento Cubierta Vegetal',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.actividadesSobreCubierta,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'codigo_SIEX' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
                order: 3,
            },
            this.numericField('anchura_libre_proyeccion_1', 'Anchura Libre Proyeccion Copa (m)', 4, tipo),
        ]);
    }

    private getCubiertasInertesFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_establecimiento_ci', 'Fecha Establecimiento Cubierta Inerte', 1, tipo, 4, this.getLenosCalendarLimits('ci')),
            this.numericField('anchura_ci', 'Anchura Cubierta Inerte (m)', 2, tipo, 4),
            this.numericField('anchura_libre_proyeccion_2', 'Anchura libre de la Proyección Copa (m)', 3, tipo, 4),
        ]);
    }

    private getPastoreoExtensivoFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_inicio_pastoreo_extensivo', 'Fecha Inicio Pastoreo Extensivo', 1, tipo, 3),
            this.calendarField('fecha_fin_pastoreo_extensivo', 'Fecha Fin Pastoreo Extensivo', 2, tipo, 3),
            {
                name: this.formType < FormType.DELETION ? 'especie' : 'nombre_especie',
                vinculatedFields: undefined,
                label: 'Especie',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.especiesAnimales,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
                order: 3,
            },
            this.textField('rega', 'REGA', 4, tipo, 3),
            this.numericField('numero_animales', 'Número de Animales', 5, tipo, 3),
        ]);
    }

    private getPastoreoIslasFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_siega_1', 'Siega: 1er Fecha Corte', 1, tipo, 3),
            this.calendarField('fecha_siega_2', 'Siega: 2do Fecha Corte', 2, tipo, 3),
            this.calendarField('fecha_siega_3', 'Siega: 3er Fecha Corte', 3, tipo, 3),
            this.calendarField('fecha_laboreo', 'Fecha Laboreo', 4, tipo, 3),
            this.calendarField('fecha_siembra', this.appName === 'avafitogest' ? 'Fecha Mantenimiento' : 'Fecha Siembra', 5, tipo, 3),
            this.textAreaField('otras_actividades', this.appName === 'avafitogest' ? 'Observaciones Mantenimiento' : 'Otras Actividades', 6, tipo, 6),
        ]);
    }

    private getArrozAvaFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_siembra', 'Fecha Siembra', 1, tipo, 3),
            this.numericField('dosis_siembra', 'Dosis Siembra', 2, tipo, 3),
            this.calendarField('fecha_arroz', 'Fecha Recolección', 3, tipo, 3),
            this.calendarField('fecha_nivelacion', 'Fecha Nivelación', 4, tipo, 3),
            this.calendarField('fecha_seca_inicio_intermitente', 'Fecha Inicio Seca Intermitente', 5, tipo, 3),
            this.calendarField('fecha_seca_fin_intermitente', 'Fecha Fin Seca Intermitente', 6, tipo, 3),
            this.calendarField('fecha_inicio_inundacion_cultivo', 'Fecha Inicio Inundación del Cultivo', 7, tipo, 3, this.getInundacionCalendarLimits('inicioCultivo')),
            this.calendarField('fecha_fin_inundacion_cultivo', 'Fecha Fin Inundación del Cultivo', 8, tipo, 3, this.getInundacionCalendarLimits('finCultivo')),
            this.calendarField('fecha_inicio_inundacion_invernal', 'Fecha Inicio Inundación Invernal', 9, tipo, 3, this.getInundacionCalendarLimits('inicioInvernal')),
            this.calendarField('fecha_fin_inundacion_invernal', 'Fecha Fin Inundación Invernal', 10, tipo, 3, this.getInundacionCalendarLimits('finInvernal')),
        ]);
    }

    private getArrozFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_arroz', 'Fecha Arroz', 1, tipo),
            {
                name: 'labor',
                vinculatedFields: undefined,
                label: 'Labor',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tareasLabor,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
                order: 2,
            },
            {
                name: 'tipo_operacion',
                vinculatedFields: undefined,
                label: 'Tipo Operación',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tipoOperacionList,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
                order: 3,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria',
                vinculatedFields: undefined,
                label: 'Maquinaria',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.maquinarias,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
                order: 4,
            },
            this.textAreaField('observaciones', 'Observaciones', 5, tipo, 6),
        ]);
    }

    private getHiddenFields(): FormField<AgroambientalModel>[] {
        return [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isVisible: true }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getDefaultClientId(),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.getDefaultClientId(),
                conditions: this.conditions({ isVisible: true }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: String(this.model.id_usuario ?? this.user.id ?? ''),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => String(this.model.id_usuario ?? this.user.id ?? ''),
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
            {
                name: 'adjunto_guia',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.adjunto_guia,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isVisible: true }),
                order: 4,
            },
            {
                name: 'nombre_mantenimiento',
                vinculatedFields: ['tipo_mantenimiento_cv'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.getSelectedActividadName(),
                conditions: this.conditions({ isVisible: true }),
                order: 5,
            },
            {
                name: 'nombre_especie',
                vinculatedFields: ['especie'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                updateValue: () => getDropdownSelectedSingle(this.especiesAnimales)?.especies_animales ?? this.model.nombre_especie ?? '',
                conditions: this.conditions({ isVisible: true }),
                order: 6,
            },
        ];
    }

    private calendarField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
        range?: { minDate?: Date; maxDate?: Date },
    ): FormField<AgroambientalModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: this.formType < FormType.DELETION ? InputType.CALENDAR : InputType.ONLY_TEXT,
                isEditable: true,
                ...(this.formType < FormType.DELETION ? {
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                        ...(range?.minDate ? { minDate: range.minDate } : {}),
                        ...(range?.maxDate ? { maxDate: range.maxDate } : {}),
                    } as any,
                } : {}),
            },
            width,
            conditions: this.conditions(),
            turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
            order,
        };
    }

    private textField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: { type: InputType.TEXT, isEditable: true },
            width,
            conditions: this.conditions(),
            turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
            order,
        };
    }

    private numericField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: { type: this.formType < FormType.DELETION ? InputType.NUMERIC : InputType.ONLY_TEXT, isEditable: true },
            width,
            conditions: this.conditions(),
            turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
            order,
        };
    }

    private textAreaField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: this.formType < FormType.DELETION ? InputType.TEXTAREA : InputType.ONLY_TEXT,
                isEditable: true,
                ...(this.formType < FormType.DELETION ? {
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                } : {}),
            },
            width,
            conditions: this.conditions(),
            turnVisible: (field): void => this.applyTipoVisibility(field, tipo),
            order,
        };
    }

    private pushFields(fields: FormField<AgroambientalModel>[]): FormField<AgroambientalModel>[] {
        for (const field of fields) {
            if (field.input.type !== InputType.HIDDEN && field.conditions.isVisible) {
                this.fieldNames.push(field);
                this.fieldLabels.push(field.label);
            }
        }

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private applyTipoVisibility(field: FormField<AgroambientalModel>, tipo?: string): void {
        const targetTipo = tipo ?? this.getFieldTipo(field);

        if (!targetTipo || this.formType === FormType.EXPORT) {
            return;
        }

        if (this.formType >= FormType.DELETION) {
            field.conditions.isVisible = this.getTipoEcorregimen() === targetTipo;
            return;
        }

        field.conditions.isVisible = this.getTipoEcorregimen() === targetTipo;
    }

    private getFieldTipo(field: FormField<AgroambientalModel>): string | undefined {
        const fieldName = String(field.name);

        if ([
            'fecha_establecimiento_cv',
            'anchura_cv',
            'tipo_mantenimiento_cv',
            'anchura_libre_proyeccion_1',
            'fecha_establecimiento_ci',
            'anchura_ci',
            'anchura_libre_proyeccion_2',
        ].includes(fieldName)) {
            return 'Leñosos';
        }

        if ([
            'fecha_inicio_pastoreo_extensivo',
            'fecha_fin_pastoreo_extensivo',
            'especie',
            'nombre_especie',
            'rega',
            'numero_animales',
            'fecha_siega_1',
            'fecha_siega_2',
            'fecha_siega_3',
            'fecha_laboreo',
            'otras_actividades',
        ].includes(fieldName)) {
            return 'Pastoreo';
        }

        if ([
            'fecha_siembra',
            'dosis_siembra',
            'fecha_arroz',
            'fecha_nivelacion',
            'fecha_seca_inicio_intermitente',
            'fecha_seca_fin_intermitente',
            'fecha_inicio_inundacion_cultivo',
            'fecha_fin_inundacion_cultivo',
            'fecha_inicio_inundacion_invernal',
            'fecha_fin_inundacion_invernal',
            'labor',
            'tipo_operacion',
            'id_maquinaria',
            'maquinaria',
            'observaciones',
        ].includes(fieldName)) {
            return 'Arroz';
        }

        return undefined;
    }

    private filterFinca(it: FilteredValue<AgroambientalModel>): boolean {
        const fincaValue = it.value as unknown as FincasModel | null;

        if (!this.hasClients || this.formType === FormType.EDITION || !this.userCanSeeClientes) {
            return true;
        }

        if (this.client?.id) {
            return String(fincaValue?.id_cliente ?? '') === String(this.client.id);
        }

        return true;
    }

    private filterSector(it: FilteredValue<AgroambientalModel>): boolean {
        const sectorValue = it.value as unknown as SectorModel | null;

        if (sectorValue === null) {
            return true;
        }

        const finca = getDropdownSelectedSingle(this.fincas) as FincasModel | null;

        return String(sectorValue.id_finca ?? '') === String(finca?.id ?? '');
    }

    private filterParcela(it: FilteredValue<AgroambientalModel>): boolean {
        const parcelaValue = it.value as unknown as ParcelasModel | null;

        if (parcelaValue === null) {
            return true;
        }

        if (parcelaValue.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectores = getDropdownSelected(this.sectores) as SectorModel | SectorModel[] | null;
        const selectedFinca = getDropdownSelectedSingle(this.fincas) as FincasModel | null;

        if (Array.isArray(selectedSectores) && selectedSectores.length > 0) {
            return selectedSectores.some((sector) => String(sector?.id ?? '') === String(parcelaValue.id_sector ?? ''));
        }

        if (selectedSectores && typeof selectedSectores === 'object') {
            return String((selectedSectores as SectorModel).id ?? '') === String(parcelaValue.id_sector ?? '');
        }

        return String(parcelaValue.id_finca ?? '') === String(selectedFinca?.id ?? '');
    }

    private getSelectedActividadName(): string {
        const selected = getDropdownSelectedSingle(this.actividadesSobreCubierta);

        return selected?.actividad ?? selected?.actividad_sobre_la_cubierta ?? this.model.nombre_mantenimiento ?? '';
    }

    private getTipoEcorregimen(): string {
        const value = this.model.tipo_ecorregimen;

        if (typeof value === 'string') {
            return value;
        }

        return '';
    }

    private getDefaultClientId(): string | undefined {
        if (StorageManager.getUser().tipo === 'comunero') {
            return String(StorageManager.getUser().id_cliente ?? '');
        }

        const clientIdFromForm = this.model.id_cliente;

        if (typeof clientIdFromForm === 'string' || typeof clientIdFromForm === 'number') {
            return String(clientIdFromForm);
        }

        if (clientIdFromForm && typeof clientIdFromForm === 'object' && 'id' in clientIdFromForm) {
            return String(clientIdFromForm.id ?? '');
        }

        return String(this.client?.id ?? '');
    }

    private getInundacionCalendarLimits(
        type: 'inicioCultivo' | 'finCultivo' | 'inicioInvernal' | 'finInvernal',
    ): { minDate?: Date; maxDate?: Date } | undefined {
        if (this.appName !== 'avafitogest' || this.formType !== FormType.CREATION) {
            return undefined;
        }

        const year = new Date().getFullYear();

        switch (type) {
        case 'inicioCultivo':
            return { minDate: new Date(year, 4, 5), maxDate: new Date(year, 4, 14) };
        case 'finCultivo':
            return { minDate: new Date(year, 7, 16), maxDate: new Date(year, 7, 24) };
        case 'inicioInvernal':
            return { minDate: new Date(year, 10, 4), maxDate: new Date(year, 10, 14) };
        case 'finInvernal':
            return { minDate: new Date(year + 1, 0, 16), maxDate: new Date(year + 1, 0, 25) };
        }
    }

    private getLenosCalendarLimits(type: 'cv' | 'ci'): { minDate?: Date; maxDate?: Date } | undefined {
        if (this.appName !== 'avafitogest' || this.formType !== FormType.CREATION) {
            return undefined;
        }

        const year = new Date().getFullYear();

        if (type === 'cv') {
            return { minDate: new Date(year, 2, 15), maxDate: new Date(year, 10, 15) };
        }

        return { minDate: new Date(year, 0, 31), maxDate: new Date(year, 4, 15) };
    }

    private ensureDefaultDropdownValues(): void {
        if (this.tipoEcorregimenList.values.length === 0) {
            this.tipoEcorregimenList = list<string>('...', 'Leñosos', 'Arroz', 'Pastoreo');
        }
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
